import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { 
  ChevronDown, 
  ChevronRight, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Clock,
  RefreshCw
} from 'lucide-react';
import { useSyncStore } from '../stores/syncStore';

export default function DataSyncLogs() {
  const { activeSyncs, getSyncDuration } = useSyncStore();
  const [expandedSource, setExpandedSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncData, setSyncData] = useState(null);
  const [matrixDays, setMatrixDays] = useState(30);

  const fetchDataSyncLogs = async () => {
    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        setLoading(false);
        return;
      }

      // Use generic endpoint that returns all sources. Request a large window so the matrix
      // can cover historical gaps.
      const response = await fetch('https://api.glowytics.com/api/data-sync-logs?limit=50000', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data sync logs');
      }

      const raw = await response.json();
      // Support both shapes:
      // - Array of logs (google-ads endpoint style)
      // - { logs: [...] } (generic endpoint style)
      const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.logs) ? raw.logs : []);
      // Normalize field names to camelCase expected by UI
      const data = arr.map(row => ({
        dataSource: row.dataSource || row.data_source || 'UNKNOWN',
        tableName: row.tableName || row.table_name || null,
        syncStartTime: row.syncStartTime || row.sync_start_time || row.createdAt || row.created_at,
        durationSeconds: row.durationSeconds ?? row.duration_seconds ?? 0,
        syncStatus: row.syncStatus || row.sync_status || 'UNKNOWN',
        rowsProcessed: row.rowsProcessed ?? row.rows_processed ?? 0,
        rowsInserted: row.rowsInserted ?? row.rows_inserted ?? 0,
        rowsUpdated: row.rowsUpdated ?? row.rows_updated ?? 0,
        startDate: row.startDate || row.start_date || null,
        endDate: row.endDate || row.end_date || null,
        errorMessage: row.errorMessage || row.error_message || null,
      }));
      
      if (Array.isArray(data) && data.length > 0) {
        // Group by data source and date
        const groupedBySourceAndDate = data.reduce((acc, log) => {
          const source = log.dataSource || 'UNKNOWN';
          const date = new Date(log.syncStartTime).toISOString().split('T')[0];
          
          if (!acc[source]) {
            acc[source] = {
              logs: {},
              lastSync: null,
              status: 'success',
              totalRows: 0
            };
          }
          if (!acc[source].logs[date]) {
            acc[source].logs[date] = {
              totalSyncs: 0,
              successfulSyncs: 0,
              failedSyncs: 0,
              totalRows: 0,
              syncDetails: []
            };
          }
          
          // Update sync details
          acc[source].logs[date].syncDetails.push({
            date: log.syncStartTime,
            status: log.syncStatus === 'SUCCESS' ? 'success' : 'error',
            message: log.syncStatus === 'SUCCESS' ? 
              `Successfully synced ${log.rowsProcessed.toLocaleString()} rows` : 
              log.errorMessage || 'Sync failed',
            startDate: log.startDate,
            endDate: log.endDate,
            tableName: log.tableName,
            duration: log.durationSeconds
          });

          // Update metrics
          acc[source].logs[date].totalSyncs++;
          if (log.syncStatus === 'SUCCESS') {
            acc[source].logs[date].successfulSyncs++;
            acc[source].logs[date].totalRows += (log.rowsProcessed || 0);
            acc[source].totalRows += (log.rowsProcessed || 0);
          } else {
            acc[source].logs[date].failedSyncs++;
            // If there's an error, update the source status
            acc[source].status = 'error';
          }

          // Update last sync time if this is more recent
          const syncTime = new Date(log.syncStartTime);
          if (!acc[source].lastSync || syncTime > new Date(acc[source].lastSync)) {
            acc[source].lastSync = log.syncStartTime;
          }
          
          return acc;
        }, {});

        console.log('Processed sync data:', groupedBySourceAndDate);
        setSyncData(groupedBySourceAndDate);
      }
    } catch (error) {
      console.error('Error fetching data sync logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataSyncLogs();
  }, []);

  // Add another useEffect to refresh data when sync state changes
  useEffect(() => {
    // Refresh logs when any sync completes
    const anyActive = Object.values(activeSyncs).some(status => status === true);
    const syncTimer = anyActive ? null : setTimeout(() => {
      fetchDataSyncLogs();
    }, 2000); // Refresh 2 seconds after syncs complete
    
    return () => {
      if (syncTimer) clearTimeout(syncTimer);
    };
  }, [activeSyncs]);

  // Real-time updates for sync duration
  useEffect(() => {
    const anyActive = Object.values(activeSyncs).some(status => status === true);
    
    if (anyActive) {
      const interval = setInterval(() => {
        // Force re-render to update sync durations
        const randomUpdate = Math.random();
        setSyncData(prevData => ({
          ...prevData,
          _forceUpdate: randomUpdate
        }));
      }, 1000); // Update every second
      
      return () => clearInterval(interval);
    }
  }, [activeSyncs]);

  // Helper function to determine status with better logic
  const getSourceStatus = (platformName, platformKey) => {
    // If currently syncing
    if (activeSyncs[platformKey]) {
      return 'pending';
    }
    
    // If no sync data exists for this platform
    if (!syncData[platformName]) {
      return 'no-data';
    }
    
    // Check if there are any failed syncs
    const hasFailedSyncs = Object.values(syncData[platformName].logs || {}).some(dayData => 
      dayData.failedSyncs > 0
    );
    
    // Check if there are any successful syncs
    const hasSuccessfulSyncs = Object.values(syncData[platformName].logs || {}).some(dayData => 
      dayData.successfulSyncs > 0
    );
    
    if (hasFailedSyncs && !hasSuccessfulSyncs) {
      return 'error';
    } else if (hasSuccessfulSyncs) {
      return 'success';
    } else {
      return 'no-data';
    }
  };

  // Helper function to get sync status text
  const getSyncStatusText = (status, platformKey, lastSync) => {
    if (status === 'pending') {
      const duration = getSyncDuration(platformKey);
      if (duration) {
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `Syncing data... (${minutes}m ${seconds}s)`;
      }
      return 'Syncing data...';
    }
    
    if (status === 'no-data') {
      return 'No sync data available yet';
    }
    
    if (lastSync === 'N/A') {
      return 'No sync data available yet';
    }
    
    return `Last sync: ${new Date(lastSync).toLocaleString()}`;
  };

  const getSourceIcon = (name, sizeClass = 'w-8 h-8') => {
    const key = String(name || '').toLowerCase();
    if (key.includes('meta')) {
      return <img src="/6033716.png" alt="Meta Ads" className={`${sizeClass} object-contain`} />;
    }
    if (key.includes('google ads')) {
      return <img src="/google_ads-512.webp" alt="Google Ads" className={`${sizeClass} object-contain`} />;
    }
    if (key.includes('analytics') || key.includes('ga4')) {
      return <img src="/GA4logoicon.svg" alt="GA4" className={`${sizeClass} object-contain`} />;
    }
    if (key.includes('merchant_center') || key.includes('merchant center')) {
      return <img src="/google-merchant-centerpngg.svg" alt="Merchant Center" className={`${sizeClass} object-contain`} />;
    }
    if (key.includes('search_console') || key.includes('search console')) {
      return <img src="/search-console-icon-2025-1v33.svg" alt="Search Console" className={`${sizeClass} object-contain`} />;
    }
    // Fallback generic icon
    return <AlertCircle className={`${sizeClass} text-gray-400`} />;
  };

  const displayNameForSource = (raw) => {
    const v = String(raw || '').toUpperCase();
    if (v === 'MERCHANT_CENTER') return 'Merchant Center';
    if (v === 'SEARCH_CONSOLE') return 'Search Console';
    if (v === 'GOOGLE_ADS') return 'Google Ads';
    if (v === 'GOOGLE_ANALYTICS') return 'Google Analytics';
    if (v === 'META_ADS') return 'Meta Ads';
    return String(raw || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Build dynamic sources list from whatever exists in syncData
  const sources = syncData
    ? Object.keys(syncData).sort().map((originalKey) => {
        const platformKey = String(originalKey).toUpperCase().replace(/\s+/g, '_');
        const dayKeys = Object.keys(syncData[originalKey]?.logs || {});
        const firstDay = dayKeys[0];
        const pretty = displayNameForSource(originalKey);
        return {
          name: pretty,
          key: originalKey,
          platformKey,
          icon: getSourceIcon(pretty, 'w-8 h-8'),
          status: getSourceStatus(originalKey, platformKey),
          lastSync: syncData[originalKey]?.lastSync || 'N/A',
          logs: firstDay ? (syncData[originalKey]?.logs[firstDay]?.syncDetails || []) : []
        };
      })
    : [];

  const buildMatrixDates = () => {
    if (!syncData) return [];
    const allDates = new Set();
    Object.values(syncData).forEach((src) => {
      Object.keys(src?.logs || {}).forEach((d) => allDates.add(d));
    });
    return Array.from(allDates)
      .sort((a, b) => new Date(b) - new Date(a))
      .slice(0, matrixDays);
  };

  const matrixDates = buildMatrixDates();

  const formatMatrixDate = (dateStr) => {
    // Match the compact style in the example (e.g., 29.Nov)
    try {
      const d = new Date(`${dateStr}T00:00:00Z`);
      const day = d.toLocaleDateString('en-US', { day: '2-digit' });
      const mon = d.toLocaleDateString('en-US', { month: 'short' });
      return `${day}.${mon}`;
    } catch {
      return dateStr;
    }
  };

  const getMatrixCell = (sourceKey, dateStr) => {
    const day = syncData?.[sourceKey]?.logs?.[dateStr];
    if (!day) return { type: 'empty', label: '' };
    if ((day.failedSyncs || 0) > 0 && (day.successfulSyncs || 0) === 0) return { type: 'error', label: '✕' };
    if ((day.successfulSyncs || 0) > 0) return { type: 'success', label: '✓' };
    return { type: 'neutral', label: '—' };
  };

  // Update getStatusIcon to handle dynamic states
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'no-data':
        return <Clock className="w-5 h-5 text-gray-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  // Update getStatusBadge to match the dynamic states
  const getStatusBadge = (status, platformKey = null) => {
    const styles = {
      success: "bg-green-50 text-green-700 border-green-100",
      error: "bg-red-50 text-red-700 border-red-100",
      pending: "bg-blue-50 text-blue-700 border-blue-100",
      'no-data': "bg-gray-50 text-gray-600 border-gray-100"
    };

    const getLabel = (status, platformKey) => {
      if (status === 'pending' && platformKey) {
        const duration = getSyncDuration(platformKey);
        if (duration) {
          const minutes = Math.floor(duration / 60);
          const seconds = duration % 60;
          return `Syncing ${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
      }
      
      return status === 'success' ? 'Success' : 
             status === 'error' ? 'Error' : 
             status === 'pending' ? 'Syncing...' : 
             status === 'no-data' ? 'No Data' : 'Unknown';
    };

    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${styles[status] || styles['no-data']}`}>
        {getLabel(status, platformKey)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <Clock className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium">Data Sync Logs</h3>
          <p className="text-sm text-gray-500 mt-1">View and monitor data synchronization status</p>
        </div>
        <button 
          onClick={fetchDataSyncLogs}
          className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-150"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Active syncs summary */}
      {Object.values(activeSyncs).some(status => status === true) && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-blue-700">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="font-medium">Data sync in progress</span>
          </div>
          <p className="text-sm mt-1">
            {Object.entries(activeSyncs)
              .filter(([_, isActive]) => isActive)
              .map(([platform]) => platform.replace('_', ' '))
              .join(', ')} 
            {Object.values(activeSyncs).filter(v => v).length === 1 ? 'is' : 'are'} currently syncing.
          </p>
        </div>
      )}

      {!syncData && !loading && (
        <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
          <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <h3 className="text-lg font-medium text-gray-700">No sync data available</h3>
          <p className="text-gray-500 mt-1">No data synchronization history found.</p>
        </div>
      )}

      {/* Coverage matrix (date x platform) - moved to top */}
      {syncData && sources.length > 0 && matrixDates.length > 0 && (
        <Card className="mb-6 overflow-hidden rounded-2xl border border-gray-100 shadow-lg bg-white">
          {/* Header */}
          <div className="px-6 py-5 bg-gradient-to-r from-slate-50 to-white border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Sync Coverage Matrix</h4>
                  <p className="text-sm text-gray-500">
                    Daily sync status for each platform
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600">Show:</span>
                <select
                  className="text-sm font-medium border border-gray-200 rounded-lg px-3 py-2 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  value={matrixDays}
                  onChange={(e) => setMatrixDays(Number(e.target.value))}
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={60}>Last 60 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Table with fixed height and scroll */}
          <div className="overflow-auto" style={{ maxHeight: '420px' }}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="text-left px-5 py-4 font-semibold text-gray-700 whitespace-nowrap bg-gray-50">Date</th>
                  {sources.map((s) => (
                    <th key={s.key} className="text-center px-5 py-4 font-semibold text-gray-700 whitespace-nowrap bg-gray-50">
                      <div className="flex items-center justify-center gap-2">
                        <span className="flex-shrink-0">{getSourceIcon(s.name, 'w-5 h-5')}</span>
                        <span>{s.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {matrixDates.map((dateStr, idx) => (
                  <tr key={dateStr} className="hover:bg-blue-50/50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-gray-800 whitespace-nowrap">
                      {formatMatrixDate(dateStr)}
                    </td>
                    {sources.map((s) => {
                      const cell = getMatrixCell(s.key, dateStr);
                      return (
                        <td key={`${dateStr}-${s.key}`} className="px-5 py-3.5 text-center whitespace-nowrap">
                          {cell.type === 'success' ? (
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100">
                              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                          ) : cell.type === 'error' ? (
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-100">
                              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </span>
                          ) : cell.type === 'neutral' ? (
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100">
                              <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50">
                              <span className="w-2 h-2 rounded-full bg-gray-300"></span>
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Footer with legend */}
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-6 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100">
                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span>Success</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100">
                <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </span>
              <span>Failed</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
              </span>
              <span>No sync</span>
            </div>
            <div className="ml-auto text-gray-400">
              Showing {matrixDates.length} days • Scroll to see more
            </div>
          </div>
        </Card>
      )}

      {/* Platform sync details */}
      <div className="space-y-4">
        {sources.map((source) => (
          <Card key={source.name} className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            {/* Source Header */}
            <div 
              className="p-5 flex items-center justify-between cursor-pointer bg-white hover:bg-gray-50 transition-colors duration-150"
              onClick={() => setExpandedSource(expandedSource === source.name ? null : source.name)}
            >
              <div className="flex items-center gap-6">
                <div className="flex-shrink-0">
                  {source.icon}
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-medium text-gray-900">{source.name}</h3>
                    <div className="flex-shrink-0">
                      {getStatusIcon(source.status)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(source.status, source.platformKey)}
                    <span className="text-sm text-gray-500">
                      {getSyncStatusText(source.status, source.platformKey, source.lastSync)}
                    </span>
                  </div>
                </div>
              </div>
              {expandedSource === source.name ? 
                <ChevronDown className="w-5 h-5 text-gray-400" /> : 
                <ChevronRight className="w-5 h-5 text-gray-400" />
              }
            </div>

            {/* Logs Section */}
            {expandedSource === source.name && (
              <div className="border-t border-gray-200 bg-gray-50">
                <div className="p-5 space-y-3">
                  {syncData[source.key] && Object.entries(syncData[source.key].logs).map(([date, dayData]) => {
                    // Get the latest sync time for this day
                    const latestSync = dayData.syncDetails
                      .map(detail => new Date(detail.date))
                      .sort((a, b) => b - a)[0];
                    
                    return (
                      <div 
                        key={date}
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-150"
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon('success')}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(date).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                              <span className="text-sm text-gray-600">
                                {latestSync.toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false
                                })}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              Successfully synced {dayData.totalRows.toLocaleString()} rows
                            </p>
                            {/* Date range info */}
                            {dayData.syncDetails.length > 0 && dayData.syncDetails[0].startDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                Data range: {new Date(dayData.syncDetails[0].startDate).toLocaleDateString()} - {new Date(dayData.syncDetails[0].endDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Summary Stats */}
                  <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-3 gap-4 bg-white p-4 rounded-lg">
                    <div className="text-sm">
                      <span className="text-gray-500">Successful syncs:</span>
                      <span className="ml-1 font-medium text-gray-900">
                        {syncData[source.key]?.logs[Object.keys(syncData[source.key]?.logs || {})[0]]?.successfulSyncs || 0}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Failed syncs:</span>
                      <span className="ml-1 font-medium text-gray-900">
                        {syncData[source.key]?.logs[Object.keys(syncData[source.key]?.logs || {})[0]]?.failedSyncs || 0}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Total syncs:</span>
                      <span className="ml-1 font-medium text-gray-900">
                        {syncData[source.key]?.logs[Object.keys(syncData[source.key]?.logs || {})[0]]?.totalSyncs || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
} 